import React, { useState, useEffect, useContext } from 'react';
import { Link, useParams, useHistory } from 'react-router-dom';
import {
  Pagination,
  Modal,
  CircularProgress,
  Menu,
  MenuItem,
} from '@mui/material';
import moment from 'moment';
import format from 'date-fns/format';
import { enqueueSnackbar } from 'notistack';

// MUI Icons
import IosShareIcon from '@mui/icons-material/IosShare';
import SummarizeOutlinedIcon from '@mui/icons-material/SummarizeOutlined';
import ReplayIcon from '@mui/icons-material/Replay';
import DescriptionIcon from '@mui/icons-material/Description';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import SearchIcon from '@mui/icons-material/Search';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';

// API & Context
import { useQueryClient } from '@tanstack/react-query';
import { GlobalContext } from 'providers/GlobalProvider';
import { useGetTariffsByDatabaseId } from 'api/bitedb/tariffs/tariffs.hooks';
import {
  downloadTariffCsv,
  reRunTariffDatabase,
  TariffExportFormat,
} from 'api/bitedb/tariffs/tariffs.service';
import { useGetStatsByDatabaseId } from 'api/bitedb/stats/stats.hooks';
import { useBulkUploadEvents } from 'hooks/useSocketIO';
import {
  useGetDatabasesByGroupId,
  useDeleteDatabase,
} from 'api/bitedb/database/database.hooks';
import DeleteConfirmationModal from 'components/Analyzers/DeleteConfirmationModal';
import BulkExportModal from 'components/BiteDB/Modals/BulkExportModal';
import ToolBreadcrumb from 'components/Common/ToolBreadcrumb';
import { PaginationParams } from 'api/bitedb/tariffs/tariffs';
import ColumnPicker from 'components/Analyzers/columns/ColumnPicker';
import { useSavedColumns } from 'components/Analyzers/columns/useSavedColumns';
import {
  TARIFF_COLUMNS,
  TARIFF_TABLE_KEY,
} from 'components/Analyzers/columns/tariffColumns';

// --- Helper Functions ---
const formatHTSCode = (code: string | null | undefined): string => {
  if (!code) return '';
  const cleanCode = code.toString().replace(/\D/g, '');
  if (cleanCode.length === 10) {
    return `${cleanCode.slice(0, 4)}.${cleanCode.slice(4, 6)}.${cleanCode.slice(
      6,
    )}`;
  }
  return code;
};

// --- Export Constants & Helpers ---







const TariffRunDetailsPage: React.FC = () => {
  const { runId } = useParams<{ runId: string }>();
  const history = useHistory();
  const { cur_user, isWhiteLabel } = useContext(GlobalContext);
  const queryClient = useQueryClient();
  // "Create Report" consolidates Export + Build Report under one menu.
  const [reportMenuAnchor, setReportMenuAnchor] = useState<null | HTMLElement>(
    null,
  );

  // --- Real Data Hooks ---
  const {
    data: dbs,
    refetch: refetchDbs,
  } = useGetDatabasesByGroupId(cur_user?.selected_team?.id || '', '');
  const runDetails = Array.isArray(dbs)
    ? dbs.find((db) => db.id?.toString() === runId)
    : null;
  const { data: stats, refetch: refetchStats } = useGetStatsByDatabaseId(runId);
  const deleteDatabaseMutation = useDeleteDatabase();

  // ---- AUTHORITATIVE PROCESSING STATE ----------------------------------
  // The chunk processor sets ScreeningResult.status to 'in-progress' the
  // moment work starts and 'complete' / 'failed' when it finishes. We
  // surface that through getDatabasesByGroupId so the page never has to
  // guess. The socket events we listen for below are merely a "refetch
  // sooner" hint — they fire on DB-insert completion, not chunk-processor
  // completion, so we deliberately don't use them as a "done" signal.
  const serverScreeningStatus = (
    runDetails as any
  )?.last_screening_status as string | null | undefined;
  const serverIsProcessing = serverScreeningStatus === 'in-progress';
  const serverDidFail = serverScreeningStatus === 'failed';

  // Local override: set true immediately when the user clicks Re-run so
  // the UI flips to "Processing..." before the next refetch lands and
  // confirms the server's state. Cleared automatically once the server
  // catches up (or after a safety timeout).
  const [localProcessingLatch, setLocalProcessingLatch] = useState(false);

  const isProcessing = serverIsProcessing || localProcessingLatch;

  // --- States ---
  const [activeTab, setActiveTab] = useState('All');
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 50;
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  const [sortConfig, setSortConfig] = useState<{
    key: string;
    direction: 'asc' | 'desc';
  }>({ key: 'hts_code', direction: 'asc' });

  const handleSort = (key: string) => {
    setSortConfig((prev) => ({
      key: key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
    setCurrentPage(1);
  };

  const renderSortIndicator = (key: string) => {
    if (sortConfig.key !== key) return null;
    return sortConfig.direction === 'asc' ? (
      <ArrowUpwardIcon sx={{ fontSize: 14 }} />
    ) : (
      <ArrowDownwardIcon sx={{ fontSize: 14 }} />
    );
  };

  const { visible: visibleColumnKeys, setVisible: setVisibleColumnKeys } =
    useSavedColumns(TARIFF_TABLE_KEY, TARIFF_COLUMNS);
  const shownColumns = TARIFF_COLUMNS.filter((c) =>
    visibleColumnKeys.includes(c.key),
  );
  // The chosen columns plus the fixed HTS Code and Actions.
  const tableColSpan = shownColumns.length + 2;

  const handleColumnsChange = (next: typeof visibleColumnKeys) => {
    const sortedColumnHidden = TARIFF_COLUMNS.some(
      (c) => c.sortKey === sortConfig.key && !next.includes(c.key),
    );
    if (sortedColumnHidden) {
      setSortConfig({ key: 'hts_code', direction: 'asc' });
      setCurrentPage(1);
    }
    setVisibleColumnKeys(next);
  };

  // Export States
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [exportFileName, setExportFileName] = useState('');
  const [exportFormat, setExportFormat] = useState('BITE_DEFAULT');

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [, setUserHasMonitor] = useState(true);

  // Rotating status messages shown next to the spinner on the processing
  // card — same idea as AceRunDetailsPage. Phrased to match the tariff
  // pipeline's actual stages.
  const loadingMessages = React.useMemo(
    () => [
      'Resolving HTS code documents...',
      'Building tariff candidate set...',
      'Applying Free Trade Agreements...',
      'Calculating Chapter 99 exclusions...',
      'Computing duty math and fees...',
      'Saving updated tariff results...',
    ],
    [],
  );
  const [loadingText, setLoadingText] = useState(loadingMessages[0]);

  useEffect(() => {
    if (!isProcessing) return;
    let index = 0;
    setLoadingText(loadingMessages[0]);
    const interval = setInterval(() => {
      index = (index + 1) % loadingMessages.length;
      setLoadingText(loadingMessages[index]);
    }, 3000);
    return () => clearInterval(interval);
  }, [isProcessing, loadingMessages]);

  // --- Subscription Check ---
  useEffect(() => {
    const requiredTiers = ['pro', 'business', 'enterprise', 'tariff-only'];
    if (!isWhiteLabel && !requiredTiers.includes(cur_user.license)) {
      setUserHasMonitor(false);
    }
  }, [cur_user.license, isWhiteLabel]);

  // --- Search Debounce ---
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      setCurrentPage(1);
    }, 500);
    return () => clearTimeout(handler);
  }, [searchQuery]);

  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab]);

  // --- Fetch Paginated Lines ---
  // The colour tabs are a server-side filter (statusColor -> the status_color
  // projection on "TariffResult"). This used to request limit=5000 with
  // tab=All and then filter and slice in the browser, which on its own was
  // enough to saturate the analyzer task's CPU quota and 504 at the ALB.
  const paginationParams: PaginationParams = {
    page: currentPage,
    limit: pageSize,
    sortBy: sortConfig.key,
    sortOrder: sortConfig.direction,
    tab: 'All',
    statusColor:
      activeTab === 'All'
        ? undefined
        : (activeTab as 'Red' | 'Yellow' | 'Green'),
  };

  const {
    data: tableData,
    pagination: paginationData,
    totals,
    isLoading: loading,
    refetch: refetchTariffs,
  } = useGetTariffsByDatabaseId(runId, debouncedSearch, paginationParams);

  // Already filtered, sorted and paginated by the server.
  const displayLines = tableData || [];

  const totalPages = paginationData?.totalPages || 1;

  // --- Actions ---
  const executeDeleteRun = async () => {
    if (!runId) return;
    setIsDeleting(true);
    try {
      await deleteDatabaseMutation.mutateAsync({
        id: runId,
        groupId: cur_user?.selected_team?.id,
      });
      setDeleteModalOpen(false);
      history.push('/tariff-analyzers');
      enqueueSnackbar('Analysis deleted successfully.', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar('Failed to delete the run.', { variant: 'error' });
      setIsDeleting(false);
    }
  };

  // Refetch everything that contributes to the page (run details, tariff
  // list, stats). Used both by the poll interval and the socket-event
  // "refetch sooner" hint.
  const refreshAll = React.useCallback(async () => {
    try {
      if (runId) {
        await queryClient.invalidateQueries({ queryKey: ['tariffs', runId] });
        await queryClient.invalidateQueries({ queryKey: ['STATS', runId] });
      }
      await Promise.all([refetchDbs(), refetchTariffs(), refetchStats()]);
    } catch {
      // Swallow — next poll tick (or socket event) will retry.
    }
  }, [queryClient, refetchDbs, refetchStats, refetchTariffs, runId]);

  // Drive an active poll only while the server tells us the screening is
  // still in flight (or while the local latch is engaged for the first
  // few seconds after the user clicks Re-run, before the server's status
  // catches up). The effect re-evaluates whenever isProcessing flips, so
  // it cleanly starts/stops without any imperative state machine.
  React.useEffect(() => {
    if (!isProcessing) return;
    const POLL_INTERVAL_MS = 3000;
    const interval = setInterval(refreshAll, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [isProcessing, refreshAll]);

  // Local latch safety: if the server never flips to 'in-progress' (e.g.
  // automations is unreachable), don't leave the user stuck on
  // "Processing..." forever. Auto-release after a max wait.
  React.useEffect(() => {
    if (!localProcessingLatch) return;
    // Server caught up — release the latch.
    if (serverIsProcessing || serverScreeningStatus === 'complete' || serverDidFail) {
      setLocalProcessingLatch(false);
      return;
    }
    const t = setTimeout(() => setLocalProcessingLatch(false), 30 * 1000);
    return () => clearTimeout(t);
  }, [localProcessingLatch, serverIsProcessing, serverScreeningStatus, serverDidFail]);

  // Toast the user once when the run lands. Use a ref so we don't re-fire
  // every time runDetails re-renders.
  const lastNotifiedStatusRef = React.useRef<string | null>(null);
  React.useEffect(() => {
    if (!serverScreeningStatus) return;
    if (lastNotifiedStatusRef.current === serverScreeningStatus) return;
    // Skip the very first render so we don't toast for runs the user
    // didn't kick off this session.
    const isFirst = lastNotifiedStatusRef.current === null;
    lastNotifiedStatusRef.current = serverScreeningStatus;
    if (isFirst) return;
    if (serverScreeningStatus === 'complete') {
      enqueueSnackbar('Tariff run completed.', { variant: 'success' });
    } else if (serverScreeningStatus === 'failed') {
      enqueueSnackbar('Tariff run failed.', { variant: 'error' });
    }
  }, [serverScreeningStatus]);

  // Socket events from bulk-upload-complete fire when the DB inserts
  // finish, NOT when the chunk processor finishes calculating tariffs.
  // We use them only as a "refetch the run state sooner" hint — the
  // actual processing/complete decision is made from
  // ScreeningResult.status surfaced on runDetails.
  useBulkUploadEvents(undefined, {
    onComplete: (result) => {
      if (!runId) return;
      if (result?.databaseId?.toString() !== runId.toString()) return;
      refreshAll();
    },
    onFailed: (result) => {
      if (!runId) return;
      if (result?.databaseId?.toString() !== runId.toString()) return;
      refreshAll();
    },
  });

  const handleReRun = async () => {
    if (!runId) return;
    setLocalProcessingLatch(true);
    try {
      await reRunTariffDatabase(runId);
      enqueueSnackbar('Re-run started — results will refresh shortly.', {
        variant: 'success',
      });
      // Pull the new screening status right away so the UI flips to
      // server-truth "Processing" the moment the chunk processor picks
      // up its first chunk.
      refreshAll();
    } catch (error: any) {
      // Surface the actual reason when the server gave us one (e.g. "schema
      // out of date" or "automations unreachable") instead of a generic
      // failure toast.
      const msg =
        error?.response?.data?.message ||
        error?.message ||
        'Failed to trigger re-run. Please try again.';
      enqueueSnackbar(msg, { variant: 'error' });
      setLocalProcessingLatch(false);
    }
  };

  const handleExportTariffs = async () => {
    try {
      setIsDownloading(true);
      const { total, truncated } = await downloadTariffCsv(
        runId,
        exportFormat as TariffExportFormat,
        exportFileName.trim() ||
          `Tariff_Export_${format(new Date(), 'yyyy-MM-dd')}`,
      );

      if (truncated) {
        enqueueSnackbar(
          `Export capped: this database has ${total.toLocaleString()} rows and the file does not contain all of them.`,
          { variant: 'warning' },
        );
      } else {
        enqueueSnackbar('Successfully exported tariff data', {
          variant: 'success',
        });
      }
      setShowDownloadModal(false);
    } catch (error: any) {
      console.error(error);
      enqueueSnackbar(
        `Failed to export data: ${error?.message || 'Unknown error'}`,
        { variant: 'error' },
      );
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div className='analyzers-dashboard-root'>
      {/* --- Header --- */}
      <div style={{ marginBottom: '-20px' }}>
        <ToolBreadcrumb
          items={[
            { label: 'Tariff Analyzer', to: '/tariff-analyzers' },
            { label: runDetails?.name || 'Loading Details...' },
          ]}
        />
      </div>

      <div className='detail-header-flex'>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <h1 className='analyzers-page-title' style={{ marginBottom: 0 }}>
              {runDetails?.name || 'Loading Details...'}
            </h1>
            <span
              className={`run-badge ${
                isProcessing
                  ? 'needs-review'
                  : serverDidFail
                  ? 'failed'
                  : 'completed'
              }`}
              style={{
                marginTop: '2px',
                // Fall back to inline colors when the existing CSS class set
                // doesn't include a variant we need — keeps things visually
                // obvious without depending on stylesheet changes.
                ...(isProcessing
                  ? {
                      backgroundColor: '#fef3c7',
                      color: '#92400e',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px',
                    }
                  : serverDidFail
                  ? {
                      backgroundColor: '#fee2e2',
                      color: '#dc2626',
                    }
                  : {}),
              }}
            >
              {isProcessing && (
                <CircularProgress
                  size={12}
                  thickness={6}
                  sx={{ color: '#92400e' }}
                />
              )}
              {isProcessing
                ? 'Processing'
                : serverDidFail
                ? 'Failed'
                : 'Completed'}
            </span>
          </div>
          <div className='run-detail-header-meta'>
            Tariff Database •{' '}
            {runDetails?.created_at
              ? moment(runDetails.created_at).format('MM/DD/YYYY')
              : '--'}{' '}
            • {stats?.total_tariffs || 0} items
          </div>
        </div>
        <div className='detail-header-actions'>
          {/* Create Report consolidates the CSV export and the AI Report
              Builder shortcut under one menu instead of two buttons. Each
              item calls the exact same handler its old standalone button
              did. */}
          <button
            className='btn-outline'
            onClick={(e) => setReportMenuAnchor(e.currentTarget)}
            disabled={isProcessing}
            style={{
              opacity: isProcessing ? 0.5 : 1,
              cursor: isProcessing ? 'not-allowed' : 'pointer',
            }}
          >
            <SummarizeOutlinedIcon style={{ fontSize: 18 }} /> Create Report
          </button>
          <Menu
            anchorEl={reportMenuAnchor}
            open={!!reportMenuAnchor}
            onClose={() => setReportMenuAnchor(null)}
            PaperProps={{
              sx: {
                mt: 1,
                bgcolor: 'var(--bg-colors)',
                color: 'var(--text-primary-color)',
                border: '1px solid var(--es-soft-border)',
                '& .MuiMenuItem-root:hover': {
                  backgroundColor: 'var(--bg-hover)',
                },
              },
            }}
          >
            <MenuItem
              onClick={() => {
                setReportMenuAnchor(null);
                setExportFileName(
                  `${runDetails?.name || 'Tariff_Run'}_Export_${moment().format(
                    'YYYY-MM-DD',
                  )}`,
                );
                setExportFormat('BITE_DEFAULT');
                setShowDownloadModal(true);
              }}
            >
              <IosShareIcon style={{ fontSize: 18, marginRight: 8 }} />
              Export...
            </MenuItem>
            <MenuItem
              onClick={() => {
                setReportMenuAnchor(null);
                history.push('/report-builder', {
                  sourceRun: {
                    type: 'tariff',
                    runId: runId,
                    label: runDetails?.name || 'this tariff container',
                  },
                });
              }}
            >
              <SummarizeOutlinedIcon style={{ fontSize: 18, marginRight: 8 }} />
              Build Custom Report
            </MenuItem>
          </Menu>
          <button
            className='btn-primary'
            onClick={handleReRun}
            disabled={isProcessing}
            style={{
              opacity: isProcessing ? 0.6 : 1,
              cursor: isProcessing ? 'not-allowed' : 'pointer',
            }}
          >
            {isProcessing ? (
              <CircularProgress
                style={{
                  width: 16,
                  height: 16,
                  marginRight: 6,
                  color: '#fff',
                }}
              />
            ) : (
              <ReplayIcon style={{ fontSize: 18 }} />
            )}
            {isProcessing ? 'Processing...' : 'Re-run'}
          </button>
          <button
            className='btn-outline'
            onClick={() => setDeleteModalOpen(true)}
            disabled={isProcessing}
            style={{
              borderColor: '#ef4444',
              color: '#ef4444',
              opacity: isProcessing ? 0.5 : 1,
              cursor: isProcessing ? 'not-allowed' : 'pointer',
            }}
          >
            <DeleteOutlineIcon style={{ fontSize: 18 }} /> Delete
          </button>
        </div>
      </div>

      {/* --- KPI Grid --- */}
      <div
        className='detail-kpi-grid'
        style={{
          // Dim the numbers while a run is in flight so users don't act on
          // potentially-stale totals; values refresh automatically as the
          // polling/socket loop pulls fresh stats.
          opacity: isProcessing ? 0.55 : 1,
          transition: 'opacity 200ms ease',
        }}
      >
        <div className='detail-kpi-card'>
          <div className='kpi-card-header'>
            <DescriptionIcon style={{ fontSize: 16, color: '#4895ef' }} />
            <span className='kpi-card-label'>Total Tariffs</span>
          </div>
          <div className='kpi-card-value text-blue'>
            {stats?.total_tariffs?.toLocaleString() || 0}
          </div>
        </div>
        <div className='detail-kpi-card'>
          <div className='kpi-card-header'>
            <WarningAmberIcon style={{ fontSize: 16, color: '#f79009' }} />
            <span className='kpi-card-label'>Requires Review</span>
          </div>
          <div className='kpi-card-value text-orange'>
            {stats?.total_not_reviewed_tariffs?.toLocaleString() || 0}
          </div>
        </div>
        <div className='detail-kpi-card'>
          <div className='kpi-card-header'>
            <AttachMoneyIcon style={{ fontSize: 16, color: '#20c997' }} />
            <span className='kpi-card-label'>Total Value</span>
          </div>
          <div className='kpi-card-value text-teal'>
            {new Intl.NumberFormat('en-US', {
              style: 'currency',
              currency: 'USD',
              notation: 'compact',
              maximumFractionDigits: 1,
            }).format(totals?.productValue || 0)}
          </div>
        </div>
        <div className='detail-kpi-card'>
          <div className='kpi-card-header'>
            <AccountBalanceIcon style={{ fontSize: 16, color: '#17b26a' }} />
            <span className='kpi-card-label'>Total Duty</span>
          </div>
          <div className='kpi-card-value' style={{ color: '#17b26a' }}>
            {new Intl.NumberFormat('en-US', {
              style: 'currency',
              currency: 'USD',
              notation: 'compact',
              maximumFractionDigits: 1,
            }).format(totals?.dutyCost || 0)}
          </div>
        </div>
      </div>

      {/* --- Data Section --- */}
      <div className='analyzers-section' style={{ marginTop: '10px' }}>
        <div className='analyzers-section-header'>
          <h2 className='section-title'>Uploaded Data</h2>
          <p className='section-subtitle'>
            Your calculated tariffs. Click a row to view full details.
          </p>
        </div>

        <div
          className='data-toolbar-container'
          style={{
            opacity: isProcessing ? 0.5 : 1,
            pointerEvents: isProcessing ? 'none' : 'auto',
          }}
        >
          <div className='data-search-wrapper'>
            <SearchIcon className='data-search-icon' style={{ fontSize: 18 }} />
            <input
              type='text'
              className='data-search-input'
              placeholder='Search HTS, country, product code...'
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              disabled={isProcessing}
              style={{ cursor: isProcessing ? 'not-allowed' : 'text' }}
            />
          </div>

          <div className='data-segmented-control'>
            <button
              className={`data-segment-btn ${
                activeTab === 'All' ? 'active' : ''
              }`}
              onClick={() => setActiveTab('All')}
              disabled={isProcessing}
            >
              All Tariffs
            </button>
            <button
              className={`data-segment-btn ${
                activeTab === 'Red' ? 'active' : ''
              }`}
              onClick={() => setActiveTab('Red')}
              disabled={isProcessing}
            >
              Red
            </button>
            <button
              className={`data-segment-btn ${
                activeTab === 'Yellow' ? 'active' : ''
              }`}
              onClick={() => setActiveTab('Yellow')}
              disabled={isProcessing}
            >
              Yellow
            </button>
            <button
              className={`data-segment-btn ${
                activeTab === 'Green' ? 'active' : ''
              }`}
              onClick={() => setActiveTab('Green')}
              disabled={isProcessing}
            >
              Green
            </button>
          </div>

          <div style={{ marginLeft: 'auto' }}>
            <ColumnPicker
              columns={TARIFF_COLUMNS}
              visible={visibleColumnKeys}
              onChange={handleColumnsChange}
              disabled={isProcessing}
            />
          </div>
        </div>

        {/* --- Table --- */}
        <div className='runs-table-container acp-scroll-x'>
          <table className='run-data-table'>
            <thead>
              <tr>
                <th
                  style={{
                    width: '14%',
                    cursor: 'pointer',
                    userSelect: 'none',
                  }}
                  onClick={() => handleSort('hts_code')}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                    }}
                  >
                    HTS CODE {renderSortIndicator('hts_code')}
                  </div>
                </th>
                {shownColumns.map((column) =>
                  column.sortKey ? (
                    <th
                      key={column.key}
                      style={{
                        ...column.headerStyle,
                        cursor: 'pointer',
                        userSelect: 'none',
                      }}
                      onClick={() => handleSort(column.sortKey as string)}
                    >
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px',
                        }}
                      >
                        {column.header} {renderSortIndicator(column.sortKey)}
                      </div>
                    </th>
                  ) : (
                    <th key={column.key} style={column.headerStyle}>
                      {column.header}
                    </th>
                  ),
                )}
                <th
                  style={{ width: '4%', textAlign: 'center', minWidth: '70px' }}
                >
                  ACTIONS
                </th>
              </tr>
            </thead>
            <tbody>
              {isProcessing ? (
                /* Full-card progress state. Mirrors AceRunDetailsPage —
                   indeterminate progress bar + rotating status message,
                   shown in place of the data table so the user has a
                   clear "work is happening" signal and isn't tempted
                   to click around on stale rows. */
                <tr>
                  <td
                    colSpan={tableColSpan}
                    style={{ textAlign: 'center', padding: '60px 40px' }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: '14px',
                        color: '#6b7280',
                        maxWidth: '520px',
                        margin: '0 auto',
                      }}
                    >
                      <style>
                        {`
                          @keyframes tariff-progress-indeterminate {
                            0%   { left: -40%; width: 40%; }
                            60%  { left: 100%; width: 40%; }
                            100% { left: 100%; width: 40%; }
                          }
                        `}
                      </style>
                      <div style={{ width: '100%', textAlign: 'left' }}>
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'baseline',
                            marginBottom: '8px',
                          }}
                        >
                          <span
                            style={{
                              fontSize: '15px',
                              fontWeight: 600,
                              color: 'var(--text-primary, #111827)',
                            }}
                          >
                            Processing tariff run
                          </span>
                          <CircularProgress
                            size={16}
                            thickness={5}
                            sx={{ color: '#4895ef' }}
                          />
                        </div>
                        <div
                          style={{
                            position: 'relative',
                            height: '8px',
                            width: '100%',
                            backgroundColor: 'rgba(72, 149, 239, 0.15)',
                            borderRadius: '999px',
                            overflow: 'hidden',
                          }}
                        >
                          <div
                            style={{
                              position: 'absolute',
                              top: 0,
                              height: '100%',
                              backgroundColor: '#4895ef',
                              borderRadius: '999px',
                              animation:
                                'tariff-progress-indeterminate 1.6s ease-in-out infinite',
                            }}
                          />
                        </div>
                      </div>
                      <span
                        style={{
                          fontSize: '13px',
                          fontStyle: 'italic',
                          color: '#6b7280',
                        }}
                      >
                        {loadingText}
                      </span>
                      <span
                        style={{
                          fontSize: '12px',
                          color: '#9ca3af',
                        }}
                      >
                        The table will refresh automatically when the run
                        finishes.
                      </span>
                    </div>
                  </td>
                </tr>
              ) : loading ? (
                <tr>
                  <td
                    colSpan={tableColSpan}
                    style={{ textAlign: 'center', padding: '40px' }}
                  >
                    <CircularProgress size={30} />
                  </td>
                </tr>
              ) : displayLines.length === 0 ? (
                <tr>
                  <td
                    colSpan={tableColSpan}
                    style={{ textAlign: 'center', padding: '40px' }}
                  >
                    No tariffs found for this filter.
                  </td>
                </tr>
              ) : (
                displayLines.map((item: any, index: number) => {
                  return (
                    <tr
                      key={item.id || index}
                      onClick={() => {
                        history.push({
                          pathname: `/tariff/${item?.id}`,
                          state: {
                            objectData: {
                              ...item,
                              type: 'tariff',
                              viewType: 'Analyzer',
                            },
                          },
                        });
                      }}
                      className='hoverable-row'
                      style={{ cursor: 'pointer' }}
                    >
                      <td
                        style={{
                          verticalAlign: 'middle',
                          fontWeight: 500,
                          color: '#4895ef',
                        }}
                      >
                        <Link
                          to={{
                            pathname: `/tariff/${item?.id}`,
                            state: {
                              objectData: {
                                ...item,
                                type: 'tariff',
                                viewType: 'Analyzer',
                              },
                            },
                          }}
                          onClick={(e) => e.stopPropagation()}
                          style={{ color: '#4895ef', textDecoration: 'none' }}
                        >
                          {formatHTSCode(item?.hts_code)}
                        </Link>
                      </td>
                      {shownColumns.map((column) => (
                        <td
                          key={column.key}
                          style={{ verticalAlign: 'middle', ...column.cellStyle }}
                        >
                          {column.render(item)}
                        </td>
                      ))}
                      <td
                        style={{ verticalAlign: 'middle', textAlign: 'center' }}
                      >
                        <div
                          style={{
                            display: 'flex',
                            gap: '8px',
                            justifyContent: 'center',
                          }}
                        >
                          <EditOutlinedIcon
                            onClick={(e) => {
                              e.stopPropagation();
                            }}
                            sx={{
                              fontSize: 18,
                              color: 'var(--text-secondary)',
                              '&:hover': { color: 'var(--text-primary)' },
                            }}
                          />
                          <DeleteOutlineIcon
                            onClick={(e) => {
                              e.stopPropagation();
                            }}
                            sx={{
                              fontSize: 18,
                              color: '#ef4444',
                              '&:hover': { color: '#dc2626' },
                            }}
                          />
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>

          {!loading && totalPages > 1 && (
            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                padding: '20px',
              }}
            >
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={(_e: any, val: any) => setCurrentPage(val)}
                color='primary'
              />
            </div>
          )}
        </div>
      </div>

      {/* --- Modals --- */}
      <Modal
        open={showDownloadModal}
        onClose={() => !isDownloading && setShowDownloadModal(false)}
      >
        <div
          style={{
            outline: 'none',
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
          }}
        >
          <BulkExportModal
            handleClose={() => setShowDownloadModal(false)}
            fileName={exportFileName}
            setFileName={setExportFileName}
            downloadFormat={exportFormat}
            setDownloadFormat={setExportFormat}
            handleDownload={handleExportTariffs}
            isDownloading={isDownloading}
            databaseType='tariffs'
          />
        </div>
      </Modal>

      <DeleteConfirmationModal
        open={deleteModalOpen}
        handleClose={() => setDeleteModalOpen(false)}
        onDelete={executeDeleteRun}
        isDeleting={isDeleting}
        runName={runDetails?.name}
      />
    </div>
  );
};

export default TariffRunDetailsPage;
